"use client";

import {
  createContext,
  MutableRefObject,
  ReactElement,
  RefObject,
  useCallback,
  useRef,
  useState,
} from "react";
import Modal from "react-modal";

type ModalOpenOptions = {
  className?: string;
  confirm?: boolean;
  disabledPadding?: boolean;
  /** Runs instead of the close when the user dismisses the dialog (Escape,
   *  the overlay, the X) — for content whose way out is more than hiding
   *  it, like leaving the page it gates. */
  onDismiss?: () => void;
};

// A close attempt awaiting the user's decision in the stacked "Unsaved
// changes" confirm dialog (see `ModalConfirmDialog`). `onConfirmed` is the
// navigation to run on confirm; when absent, confirming closes the modal.
type PendingConfirm = {
  onConfirmed?: () => void;
};

type ModalContextProps = {
  isOpen: boolean;
  open: (children: ReactElement, options?: ModalOpenOptions) => void;
  close: (confirm?: boolean, onConfirmed?: () => void) => void;
  /** A user-initiated close (Escape, the overlay, the X): the `onDismiss`
   *  passed to `open()` when there is one, else `close()` — behind the
   *  "Unsaved changes" confirm when `confirm` is set. */
  dismiss: () => void;
  children: ReactElement | null;
  className: string | undefined;
  disabledPadding?: boolean;
  confirm: boolean | undefined;
  confirmRef: MutableRefObject<boolean>;
  updateConfirm: (confirm: boolean) => void;
  /** While true, user-initiated closes (Escape, the overlay, the X) are
   *  held — the schedule dialog has a write in flight; a programmatic
   *  `close()` is unaffected. */
  holdRef: MutableRefObject<boolean>;
  updateHold: (hold: boolean) => void;
  /** Restyle the open dialog panel from INSIDE the modal — for content that
   * needs a different width than the one the `open()` call site passed. */
  updateClassName: (className: string) => void;
  pendingConfirm: PendingConfirm | null;
  confirmPendingClose: () => void;
  cancelPendingClose: () => void;
  ref: RefObject<Modal | null>;
  contentRef: RefObject<HTMLDivElement | null>;
};

export const ModalContext = createContext<ModalContextProps>(
  {} as ModalContextProps,
);

export const useModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [children, setChildren] = useState<ReactElement | null>(null);
  const [className, setClassName] = useState<string | undefined>();
  const [disabledPadding, setDisabledPadding] = useState<boolean>();
  const [confirm, setConfirm] = useState<boolean | undefined>(false);
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm | null>(
    null,
  );
  const confirmRef = useRef(false);
  const onDismissRef = useRef<(() => void) | null>(null);
  const holdRef = useRef(false);
  const ref = useRef<Modal>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const clearChildrenTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const cancelPendingClear = () => {
    if (clearChildrenTimeoutRef.current) {
      clearTimeout(clearChildrenTimeoutRef.current);
      clearChildrenTimeoutRef.current = null;
    }
  };

  const scheduleClearChildren = () => {
    cancelPendingClear();
    clearChildrenTimeoutRef.current = setTimeout(() => {
      clearChildrenTimeoutRef.current = null;
      setChildren(null);
    }, 300);
  };

  const open = (children: ReactElement, options?: ModalOpenOptions) => {
    cancelPendingClear();
    setClassName(options?.className);
    setConfirm(options?.confirm);
    confirmRef.current = options?.confirm ?? false;
    onDismissRef.current = options?.onDismiss ?? null;
    setIsOpen(true);
    setChildren(children);
    setDisabledPadding(options?.disabledPadding);
    setPendingConfirm(null);
  };

  const close = (requireConfirm: boolean = false, onConfirmed?: () => void) => {
    if (requireConfirm) {
      // Don't touch the modal's children: the confirm renders in a stacked
      // dialog (`ModalConfirmDialog`), so the flow underneath stays mounted
      // and keeps its in-progress state if the user cancels.
      setPendingConfirm({ onConfirmed });
      return;
    }
    // An unconditional close supersedes any confirm still awaiting a decision
    // (e.g. an async flow closing the modal while the confirm is up).
    setPendingConfirm(null);
    if (onConfirmed) {
      onConfirmed();
    } else {
      setIsOpen(false);
      scheduleClearChildren();
    }
  };

  const confirmPendingClose = () => {
    if (!pendingConfirm) return;
    const { onConfirmed } = pendingConfirm;
    setPendingConfirm(null);
    if (onConfirmed) {
      onConfirmed();
    } else {
      setIsOpen(false);
      scheduleClearChildren();
    }
  };

  const cancelPendingClose = () => setPendingConfirm(null);

  const dismiss = () => {
    if (onDismissRef.current) {
      onDismissRef.current();
      return;
    }
    close(confirmRef.current);
  };

  const updateConfirm = useCallback((confirm: boolean) => {
    setConfirm(confirm);
    confirmRef.current = confirm;
  }, []);

  const updateHold = useCallback((hold: boolean) => {
    holdRef.current = hold;
  }, []);

  const updateClassName = useCallback((className: string) => {
    setClassName(className);
  }, []);

  return {
    open,
    close,
    dismiss,
    isOpen,
    children,
    className,
    disabledPadding,
    confirm,
    confirmRef,
    updateConfirm,
    holdRef,
    updateHold,
    updateClassName,
    pendingConfirm,
    confirmPendingClose,
    cancelPendingClose,
    ref,
    contentRef,
  };
};
